import React, { useEffect, useState, ReactNode } from 'react';
import { Box, Fade, useMediaQuery, useTheme } from '@mui/material';
import { useLocation } from 'react-router-dom';
import Header from './header/Header';
import { Sidebar } from './Sidebar';
import { TransferSidebar } from './TransferSidebar';
import AppFooter from './AppFooter';
import { useTransferSidebar } from '../../../context/TransferSidebarContext';
import { isPublicPath } from '@/routes/publicPaths';

interface LayoutProps {
  children: ReactNode;
}

export const Layout: React.FC<LayoutProps> = ({ children }) => {
  const theme = useTheme();
  const isLargeDesktop = useMediaQuery(theme.breakpoints.up('lg'));
  const isSmallScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const [sidebarOpen, setSidebarOpen] = useState(isLargeDesktop);
  const { open: transferSidebarOpen, setOpen: setTransferSidebarOpen } =
    useTransferSidebar();
  const location = useLocation();
  const isDownloadPage = location.pathname === '/download';
  const isVersionWorkspacePage =
    /^\/(?:projects|blueprints)\/[^/]+\/?$/.test(location.pathname) &&
    !location.pathname.endsWith('/upload');
  const isBlueprintEditorPage =
    /^\/blueprints\/(?:upload|[^/]+\/(?:edit|versions\/new))\/?$/.test(
      location.pathname
    );
  // `hidden` creates a non-scrolling ancestor that traps sticky children.
  // Keep editor previews anchored to the document's scroll below the header.
  const contentOverflowX = isBlueprintEditorPage
    ? 'clip'
    : isVersionWorkspacePage
      ? { xs: 'clip', md: 'hidden' }
      : 'hidden';

  useEffect(() => {
    setSidebarOpen(isLargeDesktop);
  }, [isLargeDesktop]);

  const handleToggleSidebar = () => {
    setSidebarOpen((prev) => {
      const next = !prev;

      // On mobile, opening the left sidebar should collapse the transfer sidebar.
      if (isSmallScreen && !prev && transferSidebarOpen) {
        setTransferSidebarOpen(false);
      }

      return next;
    });
  };

  // Don't render header/sidebar on login/register/callback pages
  if (isPublicPath(location.pathname) || location.pathname === '/register') {
    return <>{children}</>;
  }

  return (
    <Box
      sx={{
        display: 'flex',
        width: '100%',
        maxWidth: '100vw',
        overflowX: contentOverflowX,
      }}
    >
      <Header onToggleSidebar={handleToggleSidebar} />
      <Sidebar open={sidebarOpen} onClose={() => setSidebarOpen(false)} />
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p:
            isDownloadPage || isVersionWorkspacePage
              ? 0
              : { xs: 1.5, sm: 3, md: 4 },
          mt: isDownloadPage ? 0 : '56px',
          minWidth: 0,
          minHeight: isDownloadPage ? '100vh' : 'calc(100vh - 64px)',
          ...(isVersionWorkspacePage && {
            height: { md: 'calc(100dvh - 56px)' },
            minHeight: 0,
          }),
          display: 'flex',
          justifyContent: isDownloadPage ? 'flex-start' : 'center',
          alignItems: isDownloadPage ? 'stretch' : 'flex-start',
          width: '100%',
          maxWidth: '100%',
          overflowX: contentOverflowX,
        }}
      >
        <Fade key={location.pathname} in timeout={250}>
          <Box
            sx={{
              width: '100%',
              minHeight: '100%',
              ...(isVersionWorkspacePage && { height: { md: '100%' } }),
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <Box sx={{ flexGrow: 1, minHeight: 0 }}>{children}</Box>
            {!isVersionWorkspacePage && <AppFooter />}
          </Box>
        </Fade>
      </Box>
      <TransferSidebar />
    </Box>
  );
};
